/**
 * AP-9 — gemeinsame Canvas-Grundlage der Views C (Bus Statistics, Signal Plot).
 *
 * Beide Fenster zeichnen im Original mit GDI+ in einen Offscreen-DC und
 * blitten das Ergebnis (BusStatsWindow.cpp:172). Im Web übernimmt der
 * Compositor das Doppelpuffern; hier bleibt nur die Auflösungskorrektur für
 * HiDPI und der Zugriff auf die Palettenwerte, die Canvas nicht aus CSS
 * auflösen kann.
 */

import { onThemeChanged, themeColor } from '../ui/theme';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseColor(value: string): Rgb {
  const text = value.trim();
  if (text.startsWith('#')) {
    const hex =
      text.length === 4
        ? `${text[1]!}${text[1]!}${text[2]!}${text[2]!}${text[3]!}${text[3]!}`
        : text.slice(1, 7);
    const n = Number.parseInt(hex, 16);
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
  }
  const parts = text.match(/-?\d+(\.\d+)?/g);
  if (parts !== null && parts.length >= 3) {
    return { r: Number(parts[0]), g: Number(parts[1]), b: Number(parts[2]) };
  }
  return { r: 0, g: 0, b: 0 };
}

export function rgbCss(color: Rgb): string {
  return `rgb(${Math.round(color.r)},${Math.round(color.g)},${Math.round(color.b)})`;
}

/** `BlendColor(a, b, t)` des Originals: t = 0 liefert a, t = 1 liefert b. */
export function blend(a: string, b: string, t: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return rgbCss({
    r: ca.r + (cb.r - ca.r) * k,
    g: ca.g + (cb.g - ca.g) * k,
    b: ca.b + (cb.b - ca.b) * k,
  });
}

export interface ChartPalette {
  field: string;
  panel: string;
  border: string;
  /** Gitterlinien — im Original ein Blend aus Feld- und Rahmenfarbe. */
  grid: string;
  textPrimary: string;
  textMuted: string;
  textDim: string;
}

export function readChartPalette(): ChartPalette {
  const field = themeColor('field');
  const border = themeColor('frame-border');
  return {
    field,
    panel: themeColor('panel'),
    border,
    grid: blend(field, border, 0.5),
    textPrimary: themeColor('text-primary'),
    textMuted: themeColor('text-muted'),
    textDim: themeColor('text-dim'),
  };
}

export interface ChartCanvas {
  element: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  /** Breite/Höhe in CSS-Pixeln — die Zeichenroutinen rechnen ausschließlich damit. */
  readonly width: number;
  readonly height: number;
  palette: ChartPalette;
  /** Größe an das Layout angleichen; true, wenn sich etwas geändert hat. */
  sync(): boolean;
  dispose(): void;
}

export function createChartCanvas(className: string): ChartCanvas {
  const element = document.createElement('canvas');
  element.className = className;
  const ctx = element.getContext('2d');
  if (ctx === null) throw new Error('Canvas-2D-Kontext nicht verfügbar');

  let width = 0;
  let height = 0;
  let palette = readChartPalette();
  const stopTheme = onThemeChanged(() => {
    palette = readChartPalette();
    handle.palette = palette;
  });

  const handle: ChartCanvas = {
    element,
    ctx,
    get width() {
      return width;
    },
    get height() {
      return height;
    },
    palette,
    sync(): boolean {
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = Math.max(1, Math.round(element.clientWidth));
      const cssHeight = Math.max(1, Math.round(element.clientHeight));
      const pixelWidth = Math.round(cssWidth * dpr);
      const pixelHeight = Math.round(cssHeight * dpr);
      const changed = element.width !== pixelWidth || element.height !== pixelHeight;
      if (changed) {
        element.width = pixelWidth;
        element.height = pixelHeight;
      }
      width = cssWidth;
      height = cssHeight;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return changed;
    },
    dispose(): void {
      stopTheme();
    },
  };
  return handle;
}

/** Hintergrund + 1-px-Rahmen, wie `FillRect(FieldBackground)` + `Rectangle(FrameBorderColor)`. */
export function drawChartFrame(chart: ChartCanvas): void {
  const { ctx, width, height, palette } = chart;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = palette.field;
  ctx.fillRect(0, 0, width, height);
  ctx.lineWidth = 1;
  ctx.strokeStyle = palette.border;
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
}

/** Bündelt Neuzeichnungen auf einen Frame — mehrere Anstöße je Tick kosten nur einen Lauf. */
export function createRedrawScheduler(draw: () => void): { invalidate(): void; cancel(): void } {
  let handle = 0;
  return {
    invalidate(): void {
      if (handle !== 0) return;
      handle = requestAnimationFrame(() => {
        handle = 0;
        draw();
      });
    },
    cancel(): void {
      if (handle !== 0) cancelAnimationFrame(handle);
      handle = 0;
    },
  };
}
